import { type LoadEvent } from '@sveltejs/kit';

export type Role = 'admin' | 'instructor' | 'student' | 'admin,instructor';

export interface Course {
  id: number;
  name: string;
  code: string;
  active: boolean;
  skillCategories: SkillCategory[];
}

export interface Skill {
  id: number;
  name: string;
  description?: string;
  pointsValue: number;
  categoryId: number;
  active: boolean;
}

export interface SkillCategory {
  id: number;
  name: string;
  badge: string;
  active: boolean;
  courseId: number;
  skills: Skill[];
}

export interface AdUser {
  domainName: string;
  employeeId?: string;
  studentId?: string;
  firstName: string;
  lastName: string;
  universalId: string;
}

export interface User {
  id: number;
  firstName: string;
  lastName: string;
  studentId?: string;
  employeeId?: string;
  universalId: string;
  email: string;
  principalName: string;
  samlObjectId?: string;
  role: Role;
  researchConsent: boolean;
  active: boolean;
  created: string;
}

export interface Class {
  id: number;
  name: string;
  startDate: string;
  endDate: string;
  courseId: number;
  course: Course;
  active: boolean;
}

export interface ClinicalGroup {
  id: number;
  name: string;
  instructorId: number;
  instructor: User;
  active: boolean;
  classId: number;
}

export interface SkillLog {
  skillId: number;
  selfAssessmentLevel: number;
}

export interface Leaderboard {
  studentId: number;
  classId: number;
  clinicalGroupId?: number;
  score: number;
  isOptedIn: boolean;
  active: boolean;
  badgeProgress: SkillCategorySummary[];
  student: User;
  class: Class;
  clinicalGroup?: ClinicalGroup;
}

export type CourseListRecord = Pick<Course, 'id' | 'name' | 'code'>;

export interface UserListRecord {
  id: number;
  firstName: string;
  lastName: string;
  created: string;
}

export interface StudentListRecord extends UserListRecord {
  studentId: string;
}

export interface AdminInstructorListRecord extends UserListRecord {
  role: Role;
}

export type CourseSummary = Pick<Course, 'id' | 'name' | 'code'> & {
  skillCategoryCount: number;
  skillCount: number;
};

export type ClassSummary = Pick<Class, 'id' | 'name' | 'startDate' | 'endDate'> & {
  clinicalGroupCount?: number;
  studentCount?: number;
};

export type ClinicalGroupSummary = Pick<ClinicalGroup, 'id' | 'name'> & {
  instructorId?: number;
  instructorFirstName?: string;
  instructorLastName?: string;
  studentCount: number;
};

export interface SkillCategorySummary {
  skillCategoryId: number;
  possibleSkillCount: number;
  completedSkillCount: number;
}

export interface LeaderboardSkillsDetailsSkillItem {
  skillId: number;
  skillName: string;
  skillCategoryId: number;
  pointsValue: number;
  completed: boolean;
  lastRecordedProficiency?: number;
}

export interface UserClassSummary {
  classId: number;
  className: string;
  startDate: string;
  endDate: string;
  clinicalGroupId: string;
  clinicalGroupName: string;
}

// Representation of a leaderboard record with details of all skills of the class associated with the leaderboard
// record, including the last recorded proficiency level and whether the student has completed the skill.
export type LeaderboardSkillsDetail = Pick<
  Leaderboard,
  'studentId' | 'classId' | 'clinicalGroupId' | 'score' | 'isOptedIn' | 'badgeProgress' | 'class'
> & {
  clinicalGroupName: string;
  studentFirstName: string;
  studentLastName: string;
  skills: LeaderboardSkillsDetailsSkillItem[];
};

// Representation of the basic information of a record for rendering the leaderboard to students, based off of the
// leaderboard table, without class, clinical group, opt-in, or skill details. Adds rank.
export interface LeaderboardRank {
  rank: number;
  studentId: number;
  studentName: string;
  score: number;
}

export interface ValidationErrorResponse {
  errors: Record<string, string[]>;
}

export interface ValidationErrorResponse {
  errors: Record<string, string[]>;
}

export type BulkClassStudentUpdateAction = 'Remove' | 'AssignClinicalGroup';

export interface BulkClassStudentUpdateParameters {
  newClinicalGroupId?: number;
}

export interface BulkClassStudentUpdateModel {
  studentIds: number[];
  action: BulkClassStudentUpdateAction;
  parameters?: BulkClassStudentUpdateParameters;
}

export interface ClinicalGroupImportInfo {
  name: string;
  id?: number;
  exists: boolean;
}

export interface StudentImportIssue {
  studentId: number;
  name: string;
  message: string;
}

export interface PreparedImportFile {
  clinicalGroups: ClinicalGroupImportInfo[];
  studentCount: number;
  operationId: string;
  issues: StudentImportIssue[];
}

export type SortOrder = 'asc' | 'desc';

export class ValidationError extends Error implements ValidationErrorResponse {
  public readonly errors: Record<string, string[]>;

  constructor(message: string, errors: Record<string, string[]>) {
    super(message);
    this.name = 'validation';
    this.errors = errors;

    Object.setPrototypeOf(this, new.target.prototype);
    if (typeof Error.captureStackTrace === 'function') {
      Error.captureStackTrace(this, ValidationError);
    }
  }
}

export class UnauthorizedError extends Error {
  body: unknown;
  constructor(message: string, body?: unknown) {
    super(message);

    this.body = body;
    Object.setPrototypeOf(this, new.target.prototype);
    if (typeof Error.captureStackTrace === 'function') {
      Error.captureStackTrace(this, ValidationError);
    }
  }
}

export type SvelteFetch = LoadEvent['fetch'];
